import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Breadcrumbs, Link, Grid, Card, CardContent,
    CardActionArea, Chip, Skeleton, CardMedia, Paper, Avatar, Fade
} from '@mui/material';
import api from '../../services/api';
import { Parcours, Bloc } from '../../types/course';
import { designTokens } from '../../theme/designTokens';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { decodeHTMLEntities } from '../../utils/stringUtils';


const LearnerBlocDetails = () => {
    const { parcoursId, blocId } = useParams<{ parcoursId: string; blocId: string }>();
    const [parcours, setParcours] = useState<Parcours | null>(null);
    const [bloc, setBloc] = useState<Bloc | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchDetails();
    }, [parcoursId, blocId]);

    const fetchDetails = async () => {
        try {
            const res = await api.get('/api/course/parcours');
            const foundParcours = res.data.find((p: Parcours) => p._id === parcoursId);
            if (foundParcours) {
                setParcours(foundParcours);
                const foundBloc = foundParcours.blocs?.find((b: Bloc) => b._id === blocId);
                setBloc(foundBloc || null);
            }
            setLoading(false);
        } catch (err) {
            console.error("Error fetching details", err);
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
                <Skeleton variant="text" width={300} height={40} sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                    {[1, 2, 3].map((i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 4 }} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (!parcours || !bloc) return <Box sx={{ p: 4 }}>Bloc introuvable</Box>;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link
                    component={RouterLink}
                    underline="hover"
                    color="inherit"
                    to="/learner/parcours"
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <ArrowBackIcon sx={{ mr: 0.5, fontSize: 18 }} /> Mes Parcours
                </Link>
                <Link component={RouterLink} underline="hover" color="inherit" to={`/learner/parcours/${parcoursId}`}>
                    {decodeHTMLEntities(parcours.title)}
                </Link>
                <Typography color="text.primary" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                    {decodeHTMLEntities(bloc.title)}
                </Typography>
            </Breadcrumbs>

            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 5,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                }} />
                <Box sx={{
                    position: 'absolute',
                    bottom: -30,
                    right: 80,
                    width: 100,
                    height: 100,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Avatar sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        width: 56,
                        height: 56,
                        backdropFilter: 'blur(10px)'
                    }}>
                        <CategoryIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 800, fontFamily: 'Quicksand, sans-serif', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                            {decodeHTMLEntities(bloc.title)}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9 }}>
                            Explorez les catégories de compétences de ce bloc
                        </Typography>
                    </Box>
                </Box>
                {bloc.certificationNumber && (
                    <Chip
                        label={`Certification: ${bloc.certificationNumber}`}
                        sx={{
                            ml: 9,
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 600,
                            backdropFilter: 'blur(5px)'
                        }}
                    />
                )}
            </Paper>

            <Grid container spacing={3}>
                {bloc.categories?.slice().sort((a, b) => (a.slug || '').localeCompare(b.slug || '')).map((category, index) => !category.parentId && (
                    <Grid size={{ xs: 12, md: 6 }} key={category._id}>
                        <Fade in timeout={300 + index * 100}>
                            <Card sx={{
                                ...designTokens.glassCard,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                border: '1px solid rgba(102, 126, 234, 0.1)',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.2)',
                                    bgcolor: 'rgba(255, 255, 255, 0.95)'
                                }
                            }}>
                                <CardActionArea
                                    onClick={() => navigate(`/learner/category/${category._id}`)}
                                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', p: 0 }}
                                >
                                    {category.imageUrl ? (
                                        <CardMedia
                                            component="img"
                                            height="180"
                                            image={category.imageUrl}
                                            alt={category.title}
                                            sx={{ objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <Box sx={{
                                            height: 180,
                                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#667eea'
                                        }}>
                                            <CategoryIcon sx={{ fontSize: 64, opacity: 0.5 }} />
                                        </Box>
                                    )}
                                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#2c3e50', fontFamily: 'Quicksand, sans-serif', mb: 2 }}>
                                            {decodeHTMLEntities(category.title)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {category.courses && category.courses.length > 0 && (
                                                <Chip
                                                    label={`${category.courses?.length || 0} Cours`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'rgba(102, 126, 234, 0.08)',
                                                        color: '#667eea',
                                                        fontWeight: 700,
                                                        borderRadius: 1.5
                                                    }}
                                                />
                                            )}
                                            {category.categories && category.categories.length > 0 && (
                                                <Chip
                                                    label={`${category.categories.length} Sous-catégories`}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: 'rgba(118, 75, 162, 0.08)',
                                                        color: '#764ba2',
                                                        fontWeight: 700,
                                                        borderRadius: 1.5
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Fade>
                    </Grid>
                ))}
                {(!bloc.categories || bloc.categories.length === 0) && (
                    <Grid size={{ xs: 12 }}>
                        <Paper sx={{
                            textAlign: 'center',
                            py: 10,
                            borderRadius: 4,
                            background: 'rgba(255,255,255,0.5)',
                            border: '2px dashed #e0e0e0',
                        }}>
                            <CategoryIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" sx={{ fontFamily: 'Quicksand, sans-serif' }}>
                                Aucune catégorie n'est encore disponible dans ce bloc.
                            </Typography>
                        </Paper>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default LearnerBlocDetails;

